import { useState, type FC } from 'react';
import { Table } from '../Table';
import { Card } from '../Card';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { useMediaQuery } from '../../lib/hooks/useMediaQuery';
import { useTeamBehaviorsQuery } from '../../apiHooks/useTeamBehaviorsQuery';
import { useTeamBehaviorsData } from './hooks/useTeamBehaviorsData';
import styled from 'styled-components';
import { Icon, IconType } from '../Props/Icon/Icon';
import { Tooltip } from '../Tooltip';
import { BehaviorListMobile } from './components/BehaviorListMobile/BehaviorListMobile';
import { NudgeModal, type NudgeModalConfigs } from '../Nudge/NudgeModal';
import type { ScoreBreakdownType } from '../../lib/types/TeamBehavior';
import { BehaviorType } from './enum/BehaviorType';
import { BehaviorTypeHeader } from './components/BehaviorTypeHeader/BehaviorTypeHeader';
import { useZustandStore } from '../../appState/StoresProvider';
import { useNamedContentEntryText } from '../../lib/hooks/contentEntry/useNamedContentEntryText';
import { useSendNudgeMutation } from '../../apiHooks/useSendNudgeMutation';
import toast from 'react-hot-toast';

export const TeamBehaviorsUI: FC = () => {
  const [nudgeModalConfigs, setNudgeModalConfigs] = useState<NudgeModalConfigs>({
    isOpen: false,
  });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const behaviorData = useTeamBehaviorsQuery();
  const totalTeamMembersCount = behaviorData.data?.totalMembers || 0;
  const setSelectedBehaviorFilter = useZustandStore('teamBehaviors', (v) => v.setSelectedBehaviorFilter);

  const onClickBehavior = (behavior: ScoreBreakdownType, type: BehaviorType) => {
    setSelectedBehaviorFilter(behavior, type);
  };

  const { columns } = useTeamBehaviorsData(totalTeamMembersCount, setNudgeModalConfigs, onClickBehavior);

  // Only keep useNamedContentEntryText for toast text
  const successToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeSentSuccessfully',
  });
  const failToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeFailure',
  });

  // const theme = useTheme();

  const { mutate, isPending } = useSendNudgeMutation();

  const handleSendNudge = () => {
    if (!nudgeModalConfigs.behavior || !nudgeModalConfigs.type) {
      return;
    }

    mutate(
      {
        behavior: nudgeModalConfigs.behavior,
        type: nudgeModalConfigs.type,
      },
      {
        onSuccess: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon
                  className="toast-success"
                  icon={IconType.CHECK_CIRCLE}
                  width={20}
                  height={20}
                  fill="transparent"
                />
                <span>{successToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: '#151515',
                color: '#fff',
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
          });
        },
        onError: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon
                  className="toast-error"
                  icon={IconType.ALERT_TRIANGLE}
                  width={20}
                  height={20}
                  fill="transparent"
                />
                <span>{failToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: '#151515',
                color: '#fff',
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
          });
        },
      },
    );
  };

  return (
    <>
      <StyledCard
        borderRadius={12}
        withShadow
        withBorder
        title={
          <TitleContainer>
            <NamedContentEntryText container="span" refKey="managerSectionTitles" subKey="teamBehaviors" />
            {!isMobile && (
              <Tooltip
                offset={15}
                content={
                  <TooltipContainer>
                    <NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="tooltip" />
                  </TooltipContainer>
                }
                placement="top"
              >
                <Icon icon={IconType.INFO_TOOLTIP} width={16} height={16} fill="transparent" />
              </Tooltip>
            )}
          </TitleContainer>
        }
      >
        <TeamSize>
          <NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="teamSize" />:
          <TeamCount>{totalTeamMembersCount}</TeamCount>
        </TeamSize>

        {isMobile ? (
          <BehaviorListMobile data={behaviorData.data.teamBehaviours} totalTeamMembersCount={totalTeamMembersCount} />
        ) : (
          <>
            <Legend>
              <BehaviorTypeHeader type={BehaviorType.WEAK} />
              <BehaviorTypeHeader type={BehaviorType.NEUTRAL} />
              <BehaviorTypeHeader type={BehaviorType.STRONG} />
            </Legend>
            <Table
              data={behaviorData.data.teamBehaviours}
              columns={columns}
              insetRowHover
              roundedRows
              hideSeparator={true}
              hideHeaders
              isLoading={false}
            />
          </>
        )}
      </StyledCard>
      {nudgeModalConfigs.isOpen && (
        <NudgeModal
          nudgeConfigs={nudgeModalConfigs}
          onSendNudge={handleSendNudge}
          isSending={isPending}
          onClose={() =>
            setNudgeModalConfigs({
              isOpen: false,
            })
          }
        />
      )}
    </>
  );
};

const StyledCard = styled(Card)`
  padding: 24px;
  & > section {
    margin-top: 0;
  }

  /* Allow grid table cells to shrink so long behavior names can ellipsize */
  td {
    min-width: 0;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const TeamSize = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

const TeamCount = styled.span`
  font-weight: 600;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;
const TooltipContainer = styled.div`
  padding: 10px;
  width: 338px;
  font-size: 12px;
  text-transform: none;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

const NudgeToast = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  > .toast-success {
    path {
      stroke: ${({ theme }) => theme.colors.prototype.success};
    }
  }
  > .toast-error {
    path {
      stroke: ${({ theme }) => theme.colors.prototype.destructiveText};
    }
  }
  > svg {
    cursor: pointer;
    path {
      stroke: #fff;
    }
  }
`;
